import { BaseButton } from "@/components/ui-kit/Buttons/BaseButton";
import { Toast } from "@/components/ui-kit/Toast";
import useUserProvider from "@/core/dataProviders/providers/auth/useUserProvider";
import { addBookmark, deleteBookmark } from "@/core/store/app/AppSlice";
import { wrapper } from "@/core/store/store";
import { useAuthUrls } from "@/hooks/useAuthUrls";
import { ButtonType, FixedNavbarBtnProps } from "@/types/ui-kit/buttons";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DialogModalWindow } from "@/components/modules/Post/DialogModalWindow";

export const PostNavigationBookmarkButton: React.FC<FixedNavbarBtnProps> = ({ postTitle, postId }) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const { store } = wrapper.useWrappedStore({});

  const [user] = useUserProvider();

  const { getLoginUrl } = useAuthUrls();

  useEffect(() => {
    if (user?.user_meta.bookmarks && postId) {
      setIsBookmarked(user?.user_meta.bookmarks?.includes(postId.toString()));
    } else {
      setIsBookmarked(false);
    }
  }, [user?.user_meta.bookmarks, postId]);

  const saveToBookmarks = async () => {
    if (!postId || !user) {
      setIsDialogVisible(true);
      return;
    }

    setIsBookmarked(true);

    toast.custom((t) => <Toast id={t} text="Přidáno mezi oblíbené články" showRedirectToBookmarked />, {
      duration: 3000,
    });

    store.dispatch(addBookmark(postId));
  };

  const removeFromBookmarks = () => {
    if (!postId || !user || !user?.user_meta.bookmarks.length) return;

    setIsBookmarked(false);

    toast.custom((t) => <Toast id={t} text="Odebráno z oblíbených" />, {
      duration: 3000,
    });

    store.dispatch(deleteBookmark(postId));
  };

  const currentBookmarkButtonIcon = () => {
    if (isBookmarked) {
      return "/assets/images/icons/new-bookmark-active.svg";
    }
    return "/assets/images/icons/homepage-bookmark.svg";
  };

  const handleBookmarkButton = () => {
    if (isBookmarked) {
      removeFromBookmarks();
    } else {
      saveToBookmarks();
    }
    // article_interaction (bookmark)
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "save",
      interaction_option: postTitle ?? "article",
    });
  };

  return (
    <>
      <li className="mx-3 flex w-full items-center justify-center lg:w-auto">
        <button
          className="flex items-center justify-center rounded-full p-2 transition-colors ease-in-out hover:bg-gray-100 dark:hover:bg-white-25"
          onClick={() => handleBookmarkButton()}
        >
          <Image
            src={currentBookmarkButtonIcon()}
            alt="Bookmark Button"
            width={20}
            height={20}
            className={`${!isBookmarked ? "dark:invert" : ""} mr-[10px]`}
          />
          <span className="hidden dark:text-white lg:block">Uložit</span>
        </button>
      </li>
      <DialogModalWindow
        title="Chcete si uložit článek na později?"
        text="Přihlaste se pro přidávání článků mezi vaše Uložené články v sekci Můj výběr."
        button={<BaseButton href={getLoginUrl()} title="Přihlásit" type={ButtonType.Primary} />}
        isOpen={isDialogVisible}
        onClose={() => setIsDialogVisible(false)}
      />
    </>
  );
};
